/**
 * `WebImporterModel.ts` — in-browser GLB / text-glTF importer.
 *
 * Port of the glTF leg of `Models/ImporterModel/*` (`BaseImporterModel` +
 * `ImporterModelSelector` dispatch → `GLTFastImporter`), reduced to Three.js
 * `GLTFLoader`. Parses a `.glb` or a user-selected `.gltf` resource bundle,
 * measures it, and walks the node tree into {@link ParsedPart}s using
 * {@link deriveHierarchyId} so the render path binds every part by
 * `HierachyID`.
 *
 * The rows have the shape `BaseImporterModel.PostprocessImport` gives every
 * Unity import: a root named after the file, an empty `Origin` at `"0,0,"`,
 * and the file's content root renamed `Root` at `"0,1,"` with the model under
 * it (layout in `hierarchyId.ts`).
 *
 * Deviations (per web port rules — no TriLib/re-export):
 * - **`Root` keeps the content root's own local transform.** Unity's
 *   `PostprocessImport` resets it: it keeps the position (the "pivot"), but it
 *   discards the ROTATION and folds the scale of every single-child layer below
 *   it into `Root` (`ScaleUtil.ConsolidateWrapperScaling`). The scale fold
 *   draws the same geometry, so skipping it changes only which row holds the
 *   factor. The rotation reset turns a file whose top node is rotated (e.g. a
 *   Z-up export's `-90°` about X) on its side, and it is NOT ported. Before
 *   2026-10-08 the web wrote these same values on the same node, under the
 *   node's own name at `"0,0,"`, and both clients apply them on load.
 * - Browsers cannot inspect a picked file's siblings. The picker therefore
 *   passes all explicitly-selected `.gltf` companions together; a missing
 *   `.bin`/texture is named in the refusal.
 * - GLB bytes are uploaded verbatim. Loose `.gltf` and input `.gltfzip` files
 *   are normalized into Unity's self-contained `.gltfzip` contract.
 * - Embedded glTF-PBR materials still render natively on the web, but the
 *   import ALSO produces Unity-shaped material rows and texture objects
 *   (`gltfMaterialExport.ts`) and every mesh part names its own — the desktop
 *   client renders only those (AI-1494).
 */

import { randomUuid } from "@models/utils/uuid";
import { Object3D } from "three";
import { type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { createGltfLoader } from "@runtime/assets/gltfLoaderFactory";
import { normalizeGltfToUnityLayout } from "@runtime/assets/gltfUnityLayout";
import { exportGltfMaterialsAsync } from "@runtime/assets/gltfMaterialExport";
import {
  createGltfResourceBundle,
  createUnityGltfZipAsync,
  GltfResourceBundleError,
  normalizeBundlePath,
  parseGltfResourceBundleAsync,
  readGltfZipBundleAsync,
  resolveGltfBundleResource,
  type GltfResourceBundle,
} from "@runtime/assets/gltfResourceBundle";
import { Emitter } from "@core/events/emitter";
import {
  threePositionToSnapshotPosition,
  threeQuatToUnityEuler,
} from "@runtime/assets/snapshotRotationConversion";
import {
  CONTENT_ROOT_SNAPSHOT_NAME,
  K_ORIGIN_HIERARCHY_ID,
  ORIGIN_SNAPSHOT_NAME,
} from "@models/data/entityData";
import { deriveHierarchyId, flattenDepthFirst, ROOT_HIERARCHY_ID } from "./hierarchyId";
import { bestPlacementScale, computeModelBounds } from "./placementScale";
import type {
  IImporterModel,
  ImportCompleted,
  ImportFailed,
  ImportOptions,
  ImportProgress,
  ParsedModel,
  ParsedPart,
  Vec3Map,
} from "./IImporterModel";

/** Persisted `ImporterVersion` — `>= 1.0.1` selects the renderer's wrap path. */
const K_IMPORTER_VERSION = "1.0.1";

/** `AssetData.Scale` for `KnownUnits.Meters` (metres-per-unit = 1). */
const K_METERS_SCALE = 1;

export class WebImporterModel implements IImporterModel {
  readonly onImportProgress = new Emitter<ImportProgress>();
  readonly onImportCompleted = new Emitter<ImportCompleted>();
  readonly onImportFailed = new Emitter<ImportFailed>();

  readonly acceptedExtensions: ReadonlyArray<string> = [
    ".glb",
    ".gltf",
    ".gltf2",
    ".gltfzip",
  ];

  private readonly m_loader = createGltfLoader();

  isSupportedFile(fileName: string): boolean {
    const ext = extensionOf(fileName);
    return this.acceptedExtensions.includes(ext);
  }

  async importFromFileAsync(
    file: File,
    signal?: AbortSignal,
    opts?: ImportOptions,
  ): Promise<ParsedModel> {
    return this.importFromFilesAsync([file], signal, opts);
  }

  async importFromFilesAsync(
    files: ReadonlyArray<File>,
    signal?: AbortSignal,
    opts?: ImportOptions,
  ): Promise<ParsedModel> {
    const taskId = randomUuid();
    let fileName = files[0]?.name ?? "model";
    try {
      throwIfAborted(signal);
      const primary = choosePrimaryFile(files);
      fileName = primary.name;
      if (!this.isSupportedFile(primary.name)) {
        throw new ImportError(
          `Unsupported file type "${extensionOf(primary.name)}". Import a .glb, .gltf, or .gltfzip model.`,
        );
      }
      this.onImportProgress.emit({ taskId, progress: 0 });

      const loaded = await this.loadSelectionAsync(primary, files, taskId, signal);
      throwIfAborted(signal);
      // The parts authored below are read back by the desktop client against
      // the tree Unity builds, so the walk must see that tree: glTFast's
      // primitive layout (else a multi-primitive node's ids are off by one
      // primitive on desktop) and the scene layer stripped where Unity strips
      // it (else a single-root file's ids are off by one LEVEL on desktop).
      // See `gltfUnityLayout.ts`. Also what the cache receives on injection,
      // so the freshly placed entity binds identically.
      const layout = normalizeGltfToUnityLayout(loaded.gltf);
      if (layout.skipped.length > 0) {
        console.warn(
          `[WebImporterModel] ${fileName}: ${layout.skipped.length} multi-primitive node(s) kept the three.js layout — `
          + layout.skipped.slice(0, 5).join("; "),
        );
      }
      this.onImportProgress.emit({ taskId, progress: 0.75 });

      const displayName = fileNameWithoutExtension(fileName);
      const parsed = await this.buildParsedModel(
        loaded.gltf,
        loaded.packageBlob,
        loaded.format,
        fileName,
        displayName,
        opts,
        loaded.bundle,
        signal,
      );
      if (!parsed.hasRenderableGeometry) {
        throw new ImportError("This model has no renderable geometry.");
      }
      this.onImportProgress.emit({ taskId, progress: 1 });
      this.onImportCompleted.emit({ taskId, fileName });
      return parsed;
    } catch (err) {
      const normalized = normalizeImportError(err);
      const isCancelled = isAbortError(normalized);
      const reason = isCancelled
        ? "Import cancelled."
        : normalized instanceof ImportError
          ? normalized.message
          : `Import failed: ${normalized instanceof Error ? normalized.message : String(normalized)}`;
      this.onImportFailed.emit({ taskId, fileName, reason, isCancelled });
      throw normalized;
    }
  }

  private async loadSelectionAsync(
    primary: File,
    files: ReadonlyArray<File>,
    taskId: string,
    signal?: AbortSignal,
  ): Promise<{
    readonly gltf: GLTF;
    readonly packageBlob: Blob;
    readonly format: ParsedModel["format"];
    /** The resource bundle a text glTF was parsed from — its images' original bytes. */
    readonly bundle?: GltfResourceBundle;
  }> {
    const ext = extensionOf(primary.name);
    if (ext === ".glb") {
      const buffer = await primary.arrayBuffer();
      throwIfAborted(signal);
      this.onImportProgress.emit({ taskId, progress: 0.35 });
      try {
        const gltf = await this.m_loader.parseAsync(buffer, "");
        return { gltf, packageBlob: primary, format: "GLB" };
      } catch (err) {
        throw new ImportError(
          "This file couldn't be read as a glTF model (it may be corrupt or use unsupported compression).",
          err,
        );
      }
    }

    if (ext === ".gltfzip") {
      const bundle = await readGltfZipBundleAsync(primary, signal);
      this.onImportProgress.emit({ taskId, progress: 0.3 });
      const gltf = await parseGltfResourceBundleAsync(bundle, {
        signal,
        missingImages: "error",
      });
      this.onImportProgress.emit({ taskId, progress: 0.6 });
      const packageBlob = await createUnityGltfZipAsync(bundle, signal);
      return { gltf, packageBlob, format: "GLTF", bundle };
    }

    const entries: Array<readonly [string, Blob]> = [];
    for (let i = 0; i < files.length; i++) {
      const selected = files[i];
      entries.push([
        selectedFilePath(selected),
        selected,
      ]);
      this.onImportProgress.emit({
        taskId,
        progress: 0.3 * ((i + 1) / Math.max(1, files.length)),
      });
    }
    const bundle = createGltfResourceBundle(entries, selectedFilePath(primary));
    const gltf = await parseGltfResourceBundleAsync(bundle, {
      signal,
      missingImages: "error",
    });
    this.onImportProgress.emit({ taskId, progress: 0.6 });
    const packageBlob = await createUnityGltfZipAsync(bundle, signal);
    return { gltf, packageBlob, format: "GLTF", bundle };
  }

  private async buildParsedModel(
    gltf: GLTF,
    packageBlob: Blob,
    format: ParsedModel["format"],
    fileName: string,
    displayName: string,
    opts?: ImportOptions,
    bundle?: GltfResourceBundle,
    signal?: AbortSignal,
  ): Promise<ParsedModel> {
    const sceneRoot = gltf.scene;
    const bounds = computeModelBounds(sceneRoot);
    // Cloud-converted (Pixyz) GLBs are already normalized to metres — Unity
    // skips BestScale when ConvertedByPiXYZ. Otherwise unit-guess from bounds.
    const assetScale = opts?.forceMetersScale
      ? K_METERS_SCALE
      : bestPlacementScale(bounds.maxDimension);

    // Root snapshot: the synthetic entity root (walkRoot on the render side).
    // Fully IDENTITY, which is also what Unity writes for an import root.
    //
    // This used to carry `scale.z = -1` to cancel a Z-mirror the renderer's
    // `basisRoot` applied. Both halves are gone (task 0004): the pair
    // multiplied to the identity, so the web looked right while the -1 sat in
    // the persisted part AND snapshot rows, where the desktop client applies it
    // as a genuine negative `localScale` — mirrored geometry with inverted
    // winding. Never re-introduce a sign here to "fix" a mirror; it would be
    // writing a rendering workaround into shared cloud data.
    const nodes = flattenDepthFirst(sceneRoot);
    const exported = await exportGltfMaterialsAsync(gltf, nodes, {
      resolveUri: bundle ? (uri) => resolveGltfBundleResource(bundle, uri) : undefined,
      signal,
    });

    const parts: ParsedPart[] = [
      {
        partId: randomUuid(),
        snapshotId: randomUuid(),
        name: displayName,
        displayName,
        hierarchyId: ROOT_HIERARCHY_ID,
        isRoot: true,
        position: { x: 0, y: 0, z: 0 },
        rotationEuler: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        materialIds: [],
      },
      // The pivot marker Unity's importer adds as the root's first child
      // (`BaseImporterModel.AddOriginGo`): empty, identity, and persisted as a
      // row like any other transform, so the hierarchy lists it above `Root`.
      // Its presence is also what tells both clients the ids below are NOT the
      // legacy origin-less layout (see `hierarchyId.ts`).
      {
        partId: randomUuid(),
        snapshotId: randomUuid(),
        name: ORIGIN_SNAPSHOT_NAME,
        displayName: ORIGIN_SNAPSHOT_NAME,
        hierarchyId: K_ORIGIN_HIERARCHY_ID,
        isRoot: false,
        position: { x: 0, y: 0, z: 0 },
        rotationEuler: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        materialIds: [],
      },
    ];

    // gltf.scene + every descendant, depth-first (matches Unity part ordering).
    let triangleCount = 0;
    let hasRenderableGeometry = false;
    for (const node of nodes) {
      const tris = triangleCountOf(node);
      if (tris > 0) {
        triangleCount += tris;
        hasRenderableGeometry = true;
      }
      // The content root takes Unity's name for it whatever the file called it
      // (`WrapAndShiftOrigin`: `go.name = "Root"`). It keeps its own local
      // transform, though — see "Deviations" in the header.
      const name = node === sceneRoot
        ? CONTENT_ROOT_SNAPSHOT_NAME
        : node.name && node.name.length > 0 ? node.name : `Part ${parts.length}`;
      parts.push({
        partId: randomUuid(),
        snapshotId: randomUuid(),
        name,
        displayName: name,
        hierarchyId: deriveHierarchyId(node, sceneRoot),
        isRoot: false,
        // Inverse-convert the node's Three.js local into Unity snapshot space so
        // the renderer's forward conversion reconstructs the exact local (the
        // X-flip / Y,Z rotation-flip round-trips to identity).
        position: threePositionToSnapshotPosition(node.position) as Vec3Map,
        rotationEuler: threeQuatToUnityEuler(node.quaternion) as Vec3Map,
        scale: { x: node.scale.x, y: node.scale.y, z: node.scale.z },
        materialIds: exported.materialIdsByNode.get(node) ?? [],
      });
    }

    return {
      gltf,
      blob: packageBlob,
      fileName,
      displayName,
      format,
      assetScale,
      importerVersion: K_IMPORTER_VERSION,
      triangleCount,
      hasRenderableGeometry,
      boundsCenter: { x: bounds.center.x, y: bounds.center.y, z: bounds.center.z },
      maxDimension: bounds.maxDimension,
      parts,
      materials: exported.materials,
      textures: exported.textures,
    };
  }
}

class ImportError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = "ImportError";
  }
}

function triangleCountOf(node: Object3D): number {
  const mesh = node as unknown as {
    isMesh?: boolean;
    geometry?: { index?: { count: number } | null; attributes?: { position?: { count: number } } };
  };
  if (!mesh.isMesh || !mesh.geometry) return 0;
  const index = mesh.geometry.index;
  if (index) return Math.floor(index.count / 3);
  const position = mesh.geometry.attributes?.position;
  return position ? Math.floor(position.count / 3) : 0;
}

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot >= 0 ? fileName.slice(dot).toLowerCase() : "";
}

function choosePrimaryFile(files: ReadonlyArray<File>): File {
  const supported = files.filter((file) => {
    const ext = extensionOf(file.name);
    return ext === ".glb" || ext === ".gltf" || ext === ".gltf2" || ext === ".gltfzip";
  });
  const textRoots = supported.filter((file) => {
    const ext = extensionOf(file.name);
    return ext === ".gltf" || ext === ".gltf2";
  });
  if (textRoots.length === 1 && supported.length === 1) return textRoots[0];
  if (textRoots.length > 1) {
    throw new ImportError(
      `Select one glTF manifest at a time. Found: `
      + `${textRoots.map((file) => `"${file.name}"`).join(", ")}.`,
    );
  }
  if (supported.length === 1) return supported[0];
  if (supported.length > 1) {
    throw new ImportError(
      `Select one model at a time. Found: `
      + `${supported.map((file) => `"${file.name}"`).join(", ")}.`,
    );
  }
  throw new ImportError(
    "No .gltf manifest was selected. Select the .gltf file together with its .bin and texture files.",
  );
}

function selectedFilePath(file: File): string {
  return normalizeBundlePath(file.webkitRelativePath || file.name);
}

function normalizeImportError(err: unknown): unknown {
  if (err instanceof GltfResourceBundleError) {
    return new ImportError(err.message, err);
  }
  return err;
}

function fileNameWithoutExtension(fileName: string): string {
  const slash = Math.max(fileName.lastIndexOf("/"), fileName.lastIndexOf("\\"));
  const base = slash >= 0 ? fileName.slice(slash + 1) : fileName;
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(0, dot) : base;
}

function isAbortError(err: unknown): boolean {
  return (err instanceof DOMException || err instanceof Error) && err.name === "AbortError";
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw signal.reason instanceof Error
      ? signal.reason
      : new DOMException("Import cancelled.", "AbortError");
  }
}
